import { useEffect } from 'react';
import type { MutableRefObject } from 'react';

import { CustomReportStepKeyDTO } from '@/components/ui/Inputs/types/response/customReport';

const EMPTY_STEP_FIELD = 'empty_step';

type StepFormValues = Record<string, { stress_level_id?: string | null } | undefined>;

type InitialLoadParams = {
	organizationId?: string;
	companyId?: string;
	beraReportId: string;
	stepKey: CustomReportStepKeyDTO;
	fileId: string;
	stepId: string;
	setFieldValue: (name: (string | number)[], value: unknown) => void;
	getFieldValue: (name: (string | number)[]) => any;
	setTaskRPN: (value: number) => void;
	setActionPlanId: (value: string | null) => void;
	getBeraStepKeyResult: (params: {
		organization_id: string;
		company_id: string;
		custom_report_step_key_id: string;
		bera_report_id: string;
	}) => Promise<any>;
	prevStressLevelId: MutableRefObject<string | undefined>;
	prevFrequencyId: MutableRefObject<string | undefined>;
	prevTotalTaskDurationId: MutableRefObject<string | undefined>;
	prevJobElement: MutableRefObject<string | undefined>;
	hasLoadedInitialData: MutableRefObject<boolean>;
};

export function useStepKeyInitialLoad({
	organizationId,
	companyId,
	beraReportId,
	stepKey,
	fileId,
	stepId,
	setFieldValue,
	getFieldValue,
	setTaskRPN,
	setActionPlanId,
	getBeraStepKeyResult,
	prevStressLevelId,
	prevFrequencyId,
	prevTotalTaskDurationId,
	prevJobElement,
	hasLoadedInitialData
}: InitialLoadParams) {
	useEffect(() => {
		const canFetch = organizationId && companyId && beraReportId;
		if (!canFetch) return;

		function isStepAnswered(): boolean {
			const stepValues: StepFormValues = getFieldValue([fileId, stepId]) ?? {};
			const stepKeyValues = Object.values(stepValues);
			const result = stepKeyValues.some((stepKeyValue) => !!stepKeyValue?.stress_level_id);

			return result;
		}

		async function load() {
			try {
				const result = await getBeraStepKeyResult({
					organization_id: organizationId!,
					company_id: companyId!,
					custom_report_step_key_id: stepKey.id,
					bera_report_id: beraReportId
				});

				if (!result) {
					return;
				}

				setActionPlanId(result.action_plan_id ?? null);
				const prevValues = getFieldValue([fileId, stepId, stepKey.id]) || {};
				setFieldValue([fileId, stepId, stepKey.id], {
					...prevValues,
					step_key: !!result.stress_level_id,
					job_element: result.job_element,
					task_rpn: result.task_rpn ?? 0,
					stress_level_id: result.stress_level_id,
					frequency_id: result.frequency_id,
					total_task_duration_id: result.total_task_duration_id
				});
				setFieldValue([fileId, stepId, EMPTY_STEP_FIELD], !isStepAnswered());
				setTaskRPN(result.task_rpn ?? 0);
				prevStressLevelId.current = result.stress_level_id;
				prevFrequencyId.current = result.frequency_id;
				prevTotalTaskDurationId.current = result.total_task_duration_id;
				prevJobElement.current = result.job_element;
			} finally {
				hasLoadedInitialData.current = true;
			}
		}

		load();
	}, [
		organizationId,
		companyId,
		beraReportId,
		stepKey.id,
		fileId,
		stepId,
		setFieldValue,
		getFieldValue,
		setTaskRPN,
		setActionPlanId,
		getBeraStepKeyResult,
		prevStressLevelId,
		prevFrequencyId,
		prevTotalTaskDurationId,
		prevJobElement,
		hasLoadedInitialData
	]);
}
